/**
 * localStorage e sessionStorage que nunca lancam.
 *
 * O navegador bloqueia o armazenamento em aba anonima de alguns navegadores,
 * dentro de iframe e com cookies de terceiros bloqueados. Nesses casos so ler
 * `window.localStorage` ja lanca `SecurityError` (LTQ-WEB-3W): no boot do
 * AuthContext isso virava rejeicao sem tratamento e a tela ficava presa no
 * carregamento, inclusive em /reset-password, que nem precisa de sessao.
 *
 * Com o armazenamento normal, tudo passa direto para ele. Quando ele falha, o
 * valor fica num Map em memoria: a sessao funciona ate recarregar a pagina, em
 * vez de quebrar.
 */

export interface SafeStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
    /** Chaves gravadas (do navegador e da memoria). */
    keys(): string[];
}

export function createSafeStorage(backend: () => Storage | null | undefined): SafeStorage {
    const memoria = new Map<string, string>();

    return {
        getItem(key) {
            // A memoria so guarda a chave cuja ultima gravacao falhou no
            // navegador, entao ela e a mais recente (o navegador pode ter um
            // valor antigo, por exemplo o token anterior com a cota cheia).
            const daMemoria = memoria.get(key);
            if (daMemoria !== undefined) return daMemoria;
            try {
                return backend()?.getItem(key) ?? null;
            } catch {
                return null;
            }
        },
        setItem(key, value) {
            try {
                const storage = backend();
                if (storage) {
                    storage.setItem(key, value);
                    memoria.delete(key);
                    return;
                }
            } catch {
                // Bloqueado ou cheio: guarda em memoria e tira o valor antigo
                // do navegador, para outra aba ou um recarregamento nao lerem
                // um token ja rotacionado ou a empresa/filial anterior.
                try {
                    backend()?.removeItem(key);
                } catch {
                    // bloqueado: nao ha valor antigo acessivel
                }
            }
            memoria.set(key, value);
        },
        removeItem(key) {
            memoria.delete(key);
            try {
                backend()?.removeItem(key);
            } catch {
                // nada a remover do lado do navegador
            }
        },
        keys() {
            const todas = new Set(memoria.keys());
            try {
                const storage = backend();
                if (storage) {
                    for (let i = 0; i < storage.length; i += 1) {
                        const k = storage.key(i);
                        if (k !== null) todas.add(k);
                    }
                }
            } catch {
                // so as da memoria
            }
            return [...todas];
        },
    };
}

export const safeLocalStorage = createSafeStorage(() => window.localStorage);
export const safeSessionStorage = createSafeStorage(() => window.sessionStorage);
